import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { LifecycleStateBadge } from "@/components/shared/LifecycleStateBadge";
import {
  LIFECYCLE_DESCRIPTIONS,
  LIFECYCLE_LABELS,
  LIFECYCLE_STATES,
  lifecycleStateOf,
} from "@/constants/product";
import { productApi } from "@/resources/product";
import { ApiError } from "@/services/api.service";
import type { ProductLifecycleState, ProductListItem } from "@/types/product";

interface LifecycleStateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The row whose state is being set. `null` renders nothing — the host owns the target. */
  product: ProductListItem | null;
  /** Scopes the write; the endpoint 404s a brand outside the caller's organization. */
  brandId: number;
}

/**
 * The three failure strings, keyed on `ApiError.status` / `isNetworkError` and nothing else
 * (`components/product.md § The dialog's three failure strings, and the in-flight state`).
 *
 * They are literals rather than `error.response.data.message` for three settled reasons: this
 * route's two `abort(404)` bodies read `Brand not found or access denied.` and
 * `Hero product not found.` (copied verbatim from `ListingContentController.php:167,173`),
 * neither of which is the intent's copy and the first of which names a brand the user never
 * mentioned; a network rejection has no body at all; and the third row is the catch-all, so an
 * unclassified status can never render an empty error or a raw `"500 Internal Server Error"`
 * status line (`constraints.md § C-22`).
 */
const FAILURE_VIEW_ONLY =
  "Your account is view-only. Ask an admin to change this product's lifecycle state.";
const FAILURE_GONE = "This product is no longer available. Refresh the page and try again.";
const FAILURE_GENERIC = "Couldn't save the lifecycle state. Please try again.";

/**
 * A network rejection never got a response, so its `status` is the `ApiError` constructor's
 * 500 default rather than anything the server said — it is classified first, and lands on the
 * catch-all with every 5xx and every unmapped status.
 */
function failureMessageFor(error: unknown): string {
  if (error instanceof ApiError && !error.isNetworkError) {
    if (error.status === 403) return FAILURE_VIEW_ONLY;
    if (error.status === 404) return FAILURE_GONE;
  }
  return FAILURE_GENERIC;
}

/**
 * The single write path for the product lifecycle designation: every entry point — the table
 * row's kebab, the card's kebab, the badge's hover key — opens this one dialog
 * (`components/product.md § The Products page — four surfaces, one write path`).
 *
 * It saves only through `useSetProductLifecycleState`, which patches the cached product row in
 * place and deliberately neither toasts nor invalidates
 * (`decisions/a-lifecycle-save-patches-the-cached-product-row.md`). The toast and the failure
 * copy are this dialog's, which is why nothing here calls `handleApiError` — that helper toasts
 * (`utils/handle-error.util.ts:23-48`), and a toast would dismiss a failure the user is still
 * reading while the dialog stays open behind it.
 */
export function LifecycleStateModal({
  open,
  onOpenChange,
  product,
  brandId,
}: LifecycleStateModalProps) {
  /**
   * Seeded by the reset effect below, not by an initialiser.
   *
   * The design uses a lazy initialiser
   * (`signal-v3/src/components/shared/LifecycleStateModal.tsx:44`) and an earlier attempt copied
   * it — but that only helps when the host mounts the dialog per product, and `Products.tsx` keeps
   * one instance alive for the life of the page. There the initialiser ran once at page load with
   * `product === null` and never saw a real product, so it was inert while its comment claimed
   * otherwise. Keying the dialog on the product id would make it work, at the cost of unmounting
   * mid-save when the user cancels (`Cancel` stays enabled — `apiService` sets no timeout), which
   * is a worse trade than one frame. The effect is the single seam; it is covered both for a
   * reopen of the same product and for a reopen of a different one.
   */
  const [value, setValue] = useState<ProductLifecycleState>("unassigned");
  const [error, setError] = useState<string | null>(null);
  const mutation = productApi.useSetProductLifecycleState();

  const productId = product?.id ?? null;

  /**
   * Read by the reset effect, never subscribed to. The stored state is deliberately *not* a
   * dependency: `useSetProductLifecycleState` patches the cached row in `onMutate` and rolls it
   * back in `onError`, so a failed save would otherwise fire this effect and snap the radio back
   * off the user's choice while they are still reading the error.
   */
  const productRef = useRef(product);
  productRef.current = product;

  // Re-read the stored state every time the dialog opens, so a cancelled edit does not stick
  // (`design-input/.../prototype-extracted.md § The modal`). The previous failure goes with it.
  useEffect(() => {
    if (!open || productId === null) return;
    setValue(lifecycleStateOf(productRef.current?.lifecycle_state));
    setError(null);
  }, [open, productId]);

  if (!product) return null;

  const handleSave = () => {
    setError(null);
    mutation.mutate(
      { productId: product.id, brandId, lifecycleState: value },
      {
        // Close first, then toast — the order the design fixes
        // (`signal-v3/src/components/shared/LifecycleStateModal.tsx:51-61`).
        onSuccess: () => {
          onOpenChange(false);
          toast.success("Lifecycle state updated", {
            description:
              value === "unassigned"
                ? `"${product.name}" no longer carries a lifecycle designation.`
                : `"${product.name}" is now marked ${LIFECYCLE_LABELS[value]}.`,
          });
        },
        // The dialog stays open and renders the error inline (`intent.md § Failure paths`).
        onError: (rejection) => setError(failureMessageFor(rejection)),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Set lifecycle state</DialogTitle>
          <DialogDescription>
            Mark how actively this product is being worked. Nothing is hidden or archived — every
            product stays in Signal regardless of state.
          </DialogDescription>
        </DialogHeader>

        <RadioGroup
          value={value}
          onValueChange={(next) => setValue(next as ProductLifecycleState)}
          className="gap-3 py-2"
        >
          {LIFECYCLE_STATES.map((state) => (
            <Label
              key={state}
              htmlFor={`lifecycle-${state}`}
              className="flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3 transition-colors hover:bg-primary/5 has-[:checked]:border-primary"
            >
              <RadioGroupItem value={state} id={`lifecycle-${state}`} className="mt-0.5" />
              <span className="space-y-1">
                <span className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-foreground">
                    {LIFECYCLE_LABELS[state]}
                  </span>
                  <LifecycleStateBadge state={state} disableTooltip />
                </span>
                <span className="block text-xs font-normal text-muted-foreground">
                  {LIFECYCLE_DESCRIPTIONS[state]}
                </span>
              </span>
            </Label>
          ))}
        </RadioGroup>

        {error && (
          <p role="alert" className="text-sm font-medium text-destructive">
            {error}
          </p>
        )}

        <DialogFooter>
          {/*
            `Cancel` stays live while the save is in flight, deliberately: `apiService` is
            constructed with no `timeout` (`services/api.service.ts:180-184`), so a request that
            never gets a response hangs for the browser's own default and the user must be able to
            leave it. The radio group stays live for the same reason.
          */}
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={mutation.isPending}>
            {mutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
